import { Path } from 'paper';

import { type PointBezierHandle } from '../plugins/poly';

import { type Point } from './math';
import { offsetStroke } from './offset';

export type Polygon = Point[];

type Options = {
  isClosed?: boolean;
  strokeWidth?: number;
  strokeCap?: 'round' | 'square';
  simplify?: boolean;
};

export const pointsToPath = (
  points: Point[],
  elementX: number = 0,
  elementY: number = 0,
  bezierHandles?: PointBezierHandle[],
  options?: { isClosed?: boolean; simplify?: boolean },
): paper.Path => {
  const handles =
    bezierHandles ??
    points.map((point) => ({
      before: point,
      after: point,
    }));

  const paperPath = new Path({ insert: false });

  paperPath.moveTo([points[0][0] + elementX, points[0][1] + elementY]);
  points.forEach((_, pointIndex) => {
    if (pointIndex === points.length - 1) return;
    const point = points[pointIndex + 1];
    paperPath.cubicCurveTo(
      [
        handles[pointIndex].after[0] + elementX,
        handles[pointIndex].after[1] + elementY,
      ],
      [
        handles[pointIndex + 1].before[0] + elementX,
        handles[pointIndex + 1].before[1] + elementY,
      ],
      [point[0] + elementX, point[1] + elementY],
    );
  });

  if (options?.simplify) {
    paperPath.simplify();
  }

  if (options?.isClosed) {
    paperPath.cubicCurveTo(
      [
        handles[points.length - 1].after[0] + elementX,
        handles[points.length - 1].after[1] + elementY,
      ],
      [handles[0].before[0] + elementX, handles[0].before[1] + elementY],
      [points[0][0] + elementX, points[0][1] + elementY],
    );
    paperPath.closePath();
  }

  return paperPath;
};

export const pointsToShape = (
  points: Point[],
  elementX: number,
  elementY: number,
  bezierHandles?: PointBezierHandle[],
  options?: Options,
): string => {
  const defaultOptions: Required<Options> = {
    isClosed: false,
    strokeWidth: 15,
    strokeCap: 'round',
    simplify: false,
  };

  const allOptions: Required<Options> = {
    ...defaultOptions,
    ...options,
  };

  const paperPath = pointsToPath(points, elementX, elementY, bezierHandles, {
    isClosed: allOptions.isClosed,
    simplify: allOptions.simplify,
  });

  if (!allOptions.isClosed) {
    const lineArea = offsetStroke(
      paperPath,
      allOptions.strokeWidth / 2,
      allOptions.strokeCap,
    );
    if (lineArea) {
      return lineArea.pathData;
    }
  }

  return paperPath.pathData;
};
